import type { SajuAnalysis } from '../types/input';
import type { StrengthCross } from '../types/compatibility';
import type { StrengthLevel } from '../types/strength';

const STRONG_LEVELS: StrengthLevel[] = ['신강', '태강', '극왕'];
const WEAK_LEVELS: StrengthLevel[] = ['신약', '태약', '극약'];
const BALANCED_LEVELS: StrengthLevel[] = ['중화', '중화신강', '중화신약'];

/**
 * 두 사주의 신강/신약 교차 분석.
 *
 * 강-약 조합이 가장 좋고, 균형-균형이 보통, 강-강/약-약은 주의.
 *
 * - 강-약: 한쪽이 리드하고 한쪽이 따라가는 상보적 구조
 * - 균형: 특별한 이점도 문제도 없음
 * - 강-강: 자아 충돌 위험 (특히 양남양녀)
 * - 약-약: 추진력 부족, 서로 의지가 어려움
 */
export function analyzeStrengthCross(
  analysis1: SajuAnalysis,
  analysis2: SajuAnalysis,
): StrengthCross {
  const level1 = analysis1.dayMasterStrength.adjustedLevel ?? analysis1.dayMasterStrength.level;
  const level2 = analysis2.dayMasterStrength.adjustedLevel ?? analysis2.dayMasterStrength.level;
  const score1 = analysis1.dayMasterStrength.score;
  const score2 = analysis2.dayMasterStrength.score;

  const isStrong1 = STRONG_LEVELS.includes(level1);
  const isStrong2 = STRONG_LEVELS.includes(level2);
  const isWeak1 = WEAK_LEVELS.includes(level1);
  const isWeak2 = WEAK_LEVELS.includes(level2);
  const isBalanced1 = BALANCED_LEVELS.includes(level1);
  const isBalanced2 = BALANCED_LEVELS.includes(level2);

  let pattern: StrengthCross['pattern'];
  let harmony: StrengthCross['harmony'];

  if ((isStrong1 && isWeak2) || (isWeak1 && isStrong2)) {
    pattern = '강약';
    harmony = '좋음';
  } else if (isStrong1 && isStrong2) {
    pattern = '강강';
    harmony = '주의';
  } else if (isWeak1 && isWeak2) {
    pattern = '약약';
    harmony = '주의';
  } else if (isBalanced1 && isBalanced2) {
    pattern = '균형';
    harmony = '보통';
  } else {
    // 한쪽 균형 + 한쪽 강/약
    pattern = '균형';
    harmony = '보통';
  }

  const patternLabel: Record<StrengthCross['pattern'], string> = {
    '강약': '상보적 (한쪽이 리드, 한쪽이 보조)',
    '강강': '자아 충돌 가능 (양쪽 모두 강한 주관)',
    '약약': '추진력 부족 (서로 의지가 어려움)',
    '균형': '무난한 에너지 균형',
  };

  const description = `신강신약: ${level1}(${score1}점) ↔ ${level2}(${score2}점) — ${patternLabel[pattern]}`;

  return {
    person1Level: level1,
    person2Level: level2,
    person1Score: score1,
    person2Score: score2,
    pattern,
    harmony,
    description,
  };
}
